import "./style.scss";

import Euro from "@mui/icons-material/Euro";
import { Button } from "@mui/material";
import NotAllowHor from "assets/img/svg/notAllowHor";
import AddRowButton from "components/shared/AddRowButton";
import CheckboxQuestion from "components/shared/CheckboxQuestion";
import CurrencyIcon from "components/shared/CurrencyIcon";
import DateFormComponent from "components/shared/FormComponent/DateFormComponent";
import NumberFormComponent from "components/shared/FormComponent/NumberFormComponent";
import { StyledMenuItem, StyledSelect } from "components/shared/StyledSelect";
import TitleSeparation from "components/shared/TitleSeparation";
import type { Dispatch, SetStateAction } from "react";
import { useEffect } from "react";
import { Controller, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ETransferTermSalesStatus } from "store/transferTermSalesStore";
import { ETransferTermStatus } from "store/transferTermsStore";
import { v4 as uuidv4 } from "uuid";

import variables from "../../../assets/styles/_variables.module.scss";
import type { ITransferTermFormData } from "..";

interface ISettingsStep {
    indexStep: number;
    stepTitle: string;
    stepSubtitle: string;
    useFormData: Omit<ITransferTermFormData, "isDirty">;
    /**
     * Status chosen within form.
     */
    termStatus: ETransferTermStatus;
    setTermStatus: Dispatch<SetStateAction<ETransferTermStatus>>;
    /**
     * Follows whether the advance separates gross/net amount.
     */
    grossNotEqualNet: boolean;
    setGrossNotEqualNet: Dispatch<SetStateAction<boolean>>;
}

/**
 * 2nd step of TransferTermForm : start and end date + advancePayment.
 */
const SettingsStep = ({
    indexStep,
    stepSubtitle,
    stepTitle,
    useFormData,
    termStatus,
    setTermStatus,
    setGrossNotEqualNet,
    grossNotEqualNet,
}: ISettingsStep) => {
    const [t] = useTranslation();
    const { control, watch, errors, setError, clearErrors, setValue } = useFormData;

    const { fields, append, remove } = useFieldArray({
        name: "advancePayment",
        control,
    });

    const appendRow = () => {
        append({
            grossAmount: 0,
            netAmount: 0,
            paymentDate: null,
            isPaid: 0,
            order: fields.length,
            status: ETransferTermSalesStatus.WAITING_PAYMENT,
            id: uuidv4(),
        });
    };

    const startDate = watch("startDate");
    const endDate = watch("expirationDate");

    useEffect(() => {
        if (startDate && endDate && endDate < startDate) {
            if (!errors.expirationDate) {
                // Need to setValue in order for errors to be actually updated.
                setValue("expirationDate", endDate);
                setError && setError("expirationDate", { message: t("TransferTerms.TermForm.EndDateError") });
            }
        } else {
            clearErrors && clearErrors("expirationDate");
        }
    }, [startDate, endDate, errors]);

    return (
        <>
            <h3 className="form-section-title first">
                {" "}
                {indexStep + 1}. {stepTitle}
            </h3>
            <span className="form-section-description">{stepSubtitle}</span>
            <div className="form-fields">
                <div style={{ display: "flex", flexDirection: "column" } as React.CSSProperties}>
                    <span className="status-label">{t("TransferTerms.TermForm.TermStatus")}</span>
                    <StyledSelect value={termStatus} onChange={(e: any) => setTermStatus(e.target.value)}>
                        <StyledMenuItem value={ETransferTermStatus.UNDER_NEGOTIATION}>
                            {t("TransferTerms.TermsStatus.Negotiating")}
                        </StyledMenuItem>
                        <StyledMenuItem value={ETransferTermStatus.UP_TO_DATE}>
                            {t("TransferTerms.TermsStatus.Active")}
                        </StyledMenuItem>
                    </StyledSelect>
                </div>
                <div className="joined-fields">
                    <DateFormComponent
                        control={control}
                        controlName="startDate"
                        error={errors.startDate}
                        label={t("TransferTerms.TermForm.TermStart")}
                        required
                        maxYearDistance={100}
                    />
                    <DateFormComponent
                        control={control}
                        controlName="expirationDate"
                        error={errors.expirationDate}
                        label={t("TransferTerms.TermForm.TermEnd")}
                        required
                        maxYearDistance={100}
                    />
                </div>
                <NumberFormComponent
                    control={control}
                    controlName="initialGrossAmount"
                    error={errors.initialGrossAmount}
                    label={t("TransferTerms.TermForm.RightsBalance")}
                    float
                    disabled={termStatus !== ETransferTermStatus.UP_TO_DATE}
                    icon={<Euro sx={{ fontSize: 16 }} />}
                    infoTooltip={{ text: t("TransferTerms.TermForm.RightsBalanceTooltip"), type: "info" }}
                />
                <div className="transfer-advance-payments">
                    <TitleSeparation title={t("TransferTerms.TermForm.Advance")} />
                    <CheckboxQuestion
                        checked={grossNotEqualNet}
                        onChange={(e) => setGrossNotEqualNet((prev) => !prev)}
                        text={t("TransferTerms.TermForm.AdvanceDifference")}
                        textStyle={{ color: variables["foreground"] }}
                    />
                    <div>
                        {fields.map((payment, index) => (
                            <div className="advance-payment-line" key={`payment-${payment.id}`}>
                                <NumberFormComponent
                                    control={control}
                                    controlName={`advancePayment.${index}.grossAmount`}
                                    error={errors.advancePayment}
                                    float
                                    label={index === 0 ? t("TransferTerms.TermForm.GrossAmount") : ""}
                                    icon={<CurrencyIcon sx={{ fontSize: 16 }} />}
                                />
                                {grossNotEqualNet && (
                                    <NumberFormComponent
                                        control={control}
                                        controlName={`advancePayment.${index}.netAmount`}
                                        error={errors.advancePayment}
                                        float
                                        label={index === 0 ? t("TransferTerms.TermForm.NetAmount") : ""}
                                        icon={<CurrencyIcon sx={{ fontSize: 16 }} />}
                                    />
                                )}
                                <DateFormComponent
                                    control={control}
                                    controlName={`advancePayment.${index}.paymentDate`}
                                    label={index === 0 ? t("TransferTerms.TermForm.PaymentDate") : ""}
                                    maxYearDistance={10}
                                />
                                <Controller
                                    control={control}
                                    name={`advancePayment.${index}.isPaid`}
                                    render={({ field }) => {
                                        return (
                                            <StyledSelect
                                                {...field}
                                                buttonType
                                                noGradient
                                                sx={{
                                                    "&.MuiOutlinedInput-root": {
                                                        ".MuiOutlinedInput-input": {
                                                            padding: "10px 14px",
                                                            fontSize: "0.87rem",
                                                        },
                                                        ".MuiSvgIcon-root": {
                                                            color:
                                                                watch(`advancePayment.${index}.isPaid`) === 1
                                                                    ? "#5D9B62"
                                                                    : "#B55D5D",
                                                        },
                                                        "& > fieldset": {
                                                            border: "none",
                                                        },
                                                        "&:hover": {
                                                            "& > fieldset": {
                                                                borderColor: variables["secondary-purple"],
                                                            },
                                                        },
                                                    },
                                                    minWidth: "115px",
                                                    height: "38px",
                                                    color:
                                                        watch(`advancePayment.${index}.isPaid`) === 1
                                                            ? "#5D9B62"
                                                            : "#B55D5D",
                                                    fontFamily: "Poppins",
                                                    backgroundColor:
                                                        watch(`advancePayment.${index}.isPaid`) === 1
                                                            ? "rgba(152, 212, 158, 0.4)"
                                                            : "rgba(242, 191, 205, 0.4)",
                                                }}
                                            >
                                                <StyledMenuItem value={1} buttonColor="green">
                                                    {t("TransferTerms.TermForm.Paid")}
                                                </StyledMenuItem>
                                                <StyledMenuItem value={0} buttonColor="red">
                                                    {t("TransferTerms.TermForm.Unpaid")}
                                                </StyledMenuItem>
                                            </StyledSelect>
                                        );
                                    }}
                                />
                                {fields.length > 1 && (
                                    <Button onClick={() => remove(index)} disabled={index === 0}>
                                        <NotAllowHor color={index === 0 ? "transparent" : "#DA8787"} />
                                    </Button>
                                )}
                            </div>
                        ))}
                    </div>
                    <AddRowButton text={t("TransferTerms.TermForm.AddTier")} handleClick={appendRow} />
                </div>
            </div>
        </>
    );
};

export default SettingsStep;
